/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';

const TYPING_PHRASES = [
  "Recruitment Automation Realized",
  "AI-Powered Interview Intelligence",
  "Hire Smarter. Move Faster.",
  "Video Interviews, Automated.",
];

function TypingText() {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const phrase = TYPING_PHRASES[phraseIndex];
    let timeout;
    if (!deleting && displayed.length < phrase.length) {
      timeout = setTimeout(() => setDisplayed(phrase.slice(0, displayed.length + 1)), 55);
    } else if (!deleting && displayed.length === phrase.length) {
      timeout = setTimeout(() => setDeleting(true), 2200);
    } else if (deleting && displayed.length > 0) {
      timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 28);
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setPhraseIndex((phraseIndex + 1) % TYPING_PHRASES.length);
    }
    return () => clearTimeout(timeout);
  }, [displayed, deleting, phraseIndex]);

  return (
    <span>
      {displayed}
      <span className="cursor">|</span>
    </span>
  );
}

function AnimatedCounter({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef();
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let start = 0;
        const step = target / 60;
        const interval = setInterval(() => {
          start += step;
          if (start >= target) { setCount(target); clearInterval(interval); }
          else setCount(Math.floor(start));
        }, 16);
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

const steps = [
  { num: "01", icon: "📨", title: "Invitation", desc: "Receive a smart invite and create your account in seconds. No friction, no paperwork." },
  { num: "02", icon: "▶", title: "Get Started", desc: "Browse your assigned role and launch the AI-guided interview when you're ready." },
  { num: "03", icon: "🎙", title: "Record & Respond", desc: "Answer role-specific questions on video. Pavi's AI monitors clarity, tone, and relevance in real-time." },
  { num: "04", icon: "✓", title: "Done", desc: "Your interview is scored and delivered to recruiters instantly. No waiting, no scheduling." },
];

const logos = ["Guava", "Konvey", "Nexus HR", "Vantage"];

export default function PaviHome() {
  const [activeStep, setActiveStep] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setActiveStep(s => (s + 1) % steps.length), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ fontFamily: "'Syne', sans-serif", background: "#050510", color: "#fff", minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #050510; }
        .cursor { animation: blink 1s step-end infinite; color: #7DF9C2; }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        .grid-bg {
          background-image: linear-gradient(rgba(125,249,194,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(125,249,194,0.04) 1px, transparent 1px);
          background-size: 60px 60px;
        }
        .glow-orb {
          position: absolute; border-radius: 50%; filter: blur(120px); pointer-events: none;
        }
        .nav-link { color: rgba(255,255,255,0.65); text-decoration: none; font-size: 14px; font-weight: 500; transition: color 0.2s; letter-spacing: 0.03em; }
        .nav-link:hover { color: #7DF9C2; }
        .btn-primary {
          background: #7DF9C2; color: #050510; border: none; padding: 14px 32px;
          font-family: 'Syne', sans-serif; font-weight: 700; font-size: 14px;
          letter-spacing: 0.05em; cursor: pointer; text-decoration: none;
          display: inline-block; transition: all 0.2s; clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px));
        }
        .btn-primary:hover { background: #fff; transform: translateY(-2px); box-shadow: 0 0 40px rgba(125,249,194,0.4); }
        .btn-ghost {
          background: transparent; color: rgba(255,255,255,0.7); border: 1px solid rgba(255,255,255,0.15);
          padding: 14px 32px; font-family: 'Syne', sans-serif; font-weight: 600; font-size: 14px;
          letter-spacing: 0.05em; cursor: pointer; text-decoration: none; display: inline-flex;
          align-items: center; gap: 8px; transition: all 0.2s;
        }
        .btn-ghost:hover { border-color: #7DF9C2; color: #7DF9C2; }
        .step-card {
          border: 1px solid rgba(255,255,255,0.08); padding: 28px;
          transition: all 0.3s; cursor: pointer; position: relative; overflow: hidden;
          background: rgba(255,255,255,0.02);
        }
        .step-card.active { border-color: #7DF9C2; background: rgba(125,249,194,0.04); }
        .step-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: #7DF9C2; transform: scaleX(0); transition: transform 0.3s; transform-origin: left; }
        .step-card.active::before { transform: scaleX(1); }
        .stat-card { border: 1px solid rgba(255,255,255,0.08); padding: 32px; background: rgba(255,255,255,0.02); }
        .logo-ticker { display: flex; gap: 48px; animation: ticker 12s linear infinite; }
        @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        .section-label { font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: #7DF9C2; font-weight: 600; margin-bottom: 16px; }
        .tag { background: rgba(125,249,194,0.1); color: #7DF9C2; border: 1px solid rgba(125,249,194,0.2); padding: 6px 14px; font-size: 12px; letter-spacing: 0.1em; font-weight: 600; text-transform: uppercase; display: inline-block; margin-bottom: 24px; }
        .feature-pill { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); padding: 12px 20px; font-size: 13px; color: rgba(255,255,255,0.7); display: flex; align-items: center; gap: 10px; }
        .terminal { background: #0a0a1a; border: 1px solid rgba(125,249,194,0.2); padding: 24px; font-family: 'Courier New', monospace; font-size: 13px; }
        .terminal-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .fade-up { animation: fadeUp 0.7s ease forwards; }
        .noise { position: fixed; top:0;left:0;right:0;bottom:0; pointer-events:none; opacity:0.025; z-index:999;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
        }
        .floating { animation: float 6s ease-in-out infinite; }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
        .scan-line { position: absolute; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, #7DF9C2, transparent); animation: scan 4s linear infinite; opacity: 0.4; }
        @keyframes scan { 0%{top:0%} 100%{top:100%} }
      `}</style>

      {/* Noise overlay */}
      <div className="noise" />

      {/* NAV */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: "0 48px", height: "68px", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: scrolled ? "rgba(5,5,16,0.92)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "none",
        transition: "all 0.3s"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, background: "#7DF9C2", clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }} />
          <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20, letterSpacing: "0.05em" }}>PAVI</span>
        </div>
        <div style={{ display: "flex", gap: 40 }}>
          {["How it Works", "Features", "About"].map(l => (
            <a key={l} href="#" className="nav-link">{l}</a>
          ))}
        </div>
        <a href="#demo" className="btn-primary" style={{ padding: "10px 24px", fontSize: 13 }}>Request Demo</a>
      </nav>

      {/* HERO */}
      <section className="grid-bg" style={{ minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden", paddingTop: 68 }}>
        <div className="glow-orb" style={{ width: 600, height: 600, background: "rgba(125,249,194,0.12)", top: "10%", left: "50%", transform: "translateX(-20%)" }} />
        <div className="glow-orb" style={{ width: 400, height: 400, background: "rgba(99,102,241,0.1)", top: "50%", left: "10%" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 48px", width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
          <div className="fade-up">
            <div className="tag">AI-Powered Hiring Platform</div>
            <h1 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: "clamp(42px, 5vw, 72px)", lineHeight: 1.05, marginBottom: 24, letterSpacing: "-0.02em" }}>
              Automated<br />
              Video <span style={{ color: "#7DF9C2" }}>Intelligence</span>
            </h1>
            <p style={{ fontFamily: "DM Sans", fontSize: 18, color: "rgba(255,255,255,0.55)", lineHeight: 1.7, marginBottom: 40, minHeight: 28 }}>
              <TypingText />
            </p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <a href="#demo" className="btn-primary">Request Demo</a>
              <a href="#how-it-works" className="btn-ghost">
                <span style={{ width: 20, height: 20, background: "#7DF9C2", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ width: 0, height: 0, borderTop: "5px solid transparent", borderBottom: "5px solid transparent", borderLeft: "8px solid #050510", marginLeft: 2 }} />
                </span>
                Watch Video
              </a>
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 48, flexWrap: "wrap" }}>
              {["Real-time scoring", "Auto-transcription", "Bias detection", "ATS integration"].map(f => (
                <div key={f} className="feature-pill">
                  <span style={{ color: "#7DF9C2", fontSize: 16 }}>✓</span> {f}
                </div>
              ))}
            </div>
          </div>

          {/* Terminal card */}
          <div className="floating" style={{ position: "relative" }}>
            <div style={{ position: "absolute", inset: -1, background: "linear-gradient(135deg, #7DF9C2, #6366f1)", borderRadius: 2, filter: "blur(1px)" }} />
            <div className="terminal" style={{ position: "relative" }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                <span className="terminal-dot" style={{ background: "#ff5f57" }} />
                <span className="terminal-dot" style={{ background: "#febc2e" }} />
                <span className="terminal-dot" style={{ background: "#28c840" }} />
                <span style={{ marginLeft: "auto", fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em" }}>pavi-ai v3.2.1</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { label: "Candidate", val: "Jordan B.", color: "#7DF9C2" },
                  { label: "Role", val: "Senior Engineer", color: "#a5b4fc" },
                  { label: "Communication", val: "94 / 100", color: "#7DF9C2" },
                  { label: "Clarity Score", val: "88 / 100", color: "#7DF9C2" },
                  { label: "Confidence", val: "91 / 100", color: "#7DF9C2" },
                  { label: "Recommendation", val: "ADVANCE →", color: "#28c840" },
                ].map(row => (
                  <div key={row.label} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.05)", paddingBottom: 8 }}>
                    <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>{row.label}</span>
                    <span style={{ color: row.color, fontWeight: 600, fontSize: 13 }}>{row.val}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, padding: "12px 16px", background: "rgba(125,249,194,0.06)", border: "1px solid rgba(125,249,194,0.15)", fontSize: 12, color: "#7DF9C2" }}>
                ✓ Analysis complete — 2.3s
              </div>
              <div className="scan-line" />
            </div>
          </div>
        </div>
      </section>

      {/* LOGO TICKER */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "20px 0", overflow: "hidden" }}>
        <div style={{ display: "flex" }}>
          <div className="logo-ticker">
            {[...logos, ...logos].map((l, i) => (
              <span key={i} style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 18, color: "rgba(255,255,255,0.2)", whiteSpace: "nowrap", letterSpacing: "0.1em", textTransform: "uppercase" }}>{l}</span>
            ))}
          </div>
          <div className="logo-ticker" aria-hidden>
            {[...logos, ...logos].map((l, i) => (
              <span key={i} style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 18, color: "rgba(255,255,255,0.2)", whiteSpace: "nowrap", letterSpacing: "0.1em", textTransform: "uppercase" }}>{l}</span>
            ))}
          </div>
        </div>
      </div>

      {/* STATS */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "100px 48px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, background: "rgba(255,255,255,0.06)" }}>
          {[
            { val: 94, suffix: "%", label: "Interview Completion Rate" },
            { val: 3, suffix: "x", label: "Faster Time to Hire" },
            { val: 500, suffix: "+", label: "Companies Trust Pavi" },
            { val: 99, suffix: "%", label: "Recruiter Satisfaction" },
          ].map((s, i) => (
            <div key={i} className="stat-card" style={{ background: "#050510" }}>
              <div style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 52, color: "#7DF9C2", lineHeight: 1 }}>
                <AnimatedCounter target={s.val} suffix={s.suffix} />
              </div>
              <div style={{ fontFamily: "DM Sans", fontSize: 13, color: "rgba(255,255,255,0.45)", marginTop: 8, lineHeight: 1.5 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 48px 100px" }}>
        <div className="section-label">Process</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "start" }}>
          <div>
            <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: "clamp(32px, 3.5vw, 52px)", lineHeight: 1.1, marginBottom: 24, letterSpacing: "-0.02em" }}>
              How Pavi<br /><span style={{ color: "#7DF9C2" }}>Works</span>
            </h2>
            <p style={{ fontFamily: "DM Sans", color: "rgba(255,255,255,0.5)", lineHeight: 1.8, fontSize: 16, marginBottom: 48 }}>
              Pavi automates the talent lifecycle — giving employers full visibility into the acquisition process while candidates get a fast, fair, AI-guided experience.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {steps.map((s, i) => (
                <div key={i} className={`step-card ${activeStep === i ? "active" : ""}`} onClick={() => setActiveStep(i)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 11, color: activeStep === i ? "#7DF9C2" : "rgba(255,255,255,0.2)", letterSpacing: "0.15em" }}>{s.num}</span>
                    <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16 }}>{s.title}</span>
                  </div>
                  {activeStep === i && (
                    <p style={{ fontFamily: "DM Sans", fontSize: 14, color: "rgba(255,255,255,0.55)", marginTop: 12, lineHeight: 1.7 }}>{s.desc}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <div className="glow-orb" style={{ width: 300, height: 300, background: "rgba(125,249,194,0.08)", top: "20%", left: "20%" }} />
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", padding: 48, position: "relative" }}>
              <div style={{ fontSize: 64, textAlign: "center", marginBottom: 24 }}>{steps[activeStep].icon}</div>
              <h3 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 28, textAlign: "center", marginBottom: 16 }}>{steps[activeStep].title}</h3>
              <p style={{ fontFamily: "DM Sans", color: "rgba(255,255,255,0.55)", textAlign: "center", lineHeight: 1.8, fontSize: 15 }}>{steps[activeStep].desc}</p>
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 32 }}>
                {steps.map((_, i) => (
                  <div key={i} onClick={() => setActiveStep(i)} style={{ width: i === activeStep ? 24 : 8, height: 8, background: i === activeStep ? "#7DF9C2" : "rgba(255,255,255,0.15)", cursor: "pointer", transition: "all 0.3s" }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "100px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
          <div>
            <div className="section-label">About Pavi</div>
            <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: "clamp(32px, 3.5vw, 52px)", lineHeight: 1.1, marginBottom: 24, letterSpacing: "-0.02em" }}>
              Human-first.<br /><span style={{ color: "#7DF9C2" }}>Tech-enabled.</span>
            </h2>
            <p style={{ fontFamily: "DM Sans", color: "rgba(255,255,255,0.55)", lineHeight: 1.8, fontSize: 16, marginBottom: 24 }}>
              PAVI is a predictive automated video intelligence SaaS platform for global employers. We're on a mission to intertwine talent acquisition with end-to-end, technology-enabled, human-first workforce capabilities.
            </p>
            <p style={{ fontFamily: "DM Sans", color: "rgba(255,255,255,0.4)", lineHeight: 1.8, fontSize: 15 }}>
              Under our shared-services umbrella we offer a comprehensive recruitment talent suite for hyper-growth companies.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, background: "rgba(255,255,255,0.06)" }}>
            {[
              { icon: "🤖", title: "AI Scoring", desc: "Real-time analysis of communication, clarity, and confidence." },
              { icon: "⚡", title: "Instant Results", desc: "Recruiters receive scored interviews in under 3 seconds." },
              { icon: "🎯", title: "Bias-Free", desc: "Standardized scoring eliminates unconscious bias." },
              { icon: "🔗", title: "ATS Ready", desc: "Integrates with all major applicant tracking systems." },
            ].map((f, i) => (
              <div key={i} style={{ background: "#050510", padding: 28 }}>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{f.icon}</div>
                <div style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 15, marginBottom: 8 }}>{f.title}</div>
                <div style={{ fontFamily: "DM Sans", fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="demo" style={{ padding: "100px 48px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div className="glow-orb" style={{ width: 500, height: 500, background: "rgba(125,249,194,0.1)", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }} />
        <div style={{ position: "relative", maxWidth: 680, margin: "0 auto" }}>
          <div className="tag">Get Started</div>
          <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: "clamp(36px, 4vw, 60px)", lineHeight: 1.1, marginBottom: 24, letterSpacing: "-0.02em" }}>
            Ready to transform<br />your <span style={{ color: "#7DF9C2" }}>hiring process?</span>
          </h2>
          <p style={{ fontFamily: "DM Sans", color: "rgba(255,255,255,0.5)", fontSize: 17, marginBottom: 48, lineHeight: 1.7 }}>
            Join 500+ companies using Pavi to hire smarter, faster, and fairer.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#" className="btn-primary" style={{ fontSize: 15, padding: "16px 40px" }}>Request a Demo</a>
            <a href="#" className="btn-ghost" style={{ fontSize: 15, padding: "16px 40px" }}>Learn More</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "40px 48px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 22, height: 22, background: "#7DF9C2", clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }} />
          <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 16, letterSpacing: "0.05em" }}>PAVI</span>
        </div>
        <span style={{ fontFamily: "DM Sans", fontSize: 13, color: "rgba(255,255,255,0.25)" }}>© 2026 Pavi. All rights reserved.</span>
        <div style={{ display: "flex", gap: 32 }}>
          {["Privacy", "Terms", "Contact"].map(l => (
            <a key={l} href="#" className="nav-link" style={{ fontSize: 13 }}>{l}</a>
          ))}
        </div>
      </footer>
    </div>
  );
}